import React, { useCallback, useEffect, useMemo, useState } from 'react';
import M_NOT_BASIC_02 from './M_NOT_BASIC_02';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { CommonActions } from '../../../Reducer/CommonReducer';
import { NavBarInfoActions } from '../../../Reducer/NavBarInfoReducer';
import { BottomSheetActions } from '../../../Reducer/BottomSheetReducer';
import { showSnackbar } from '../../../Reducer/SnackbarReducer';
import { createInitial } from '../../../util';
import { M_NOT_BASIC_02_Schema } from './M_NOT_BASIC_02_Schema';
import {
  useDelete1,
  useSave1,
  useSave2,
  useSearch1,
  useSearch2,
  fetchCompanySites,
  fetchDepartments,
  resolveNoticeFcmTargetIds,
} from './M_NOT_BASIC_02_Query';
import useFileUpload from '../../../Hooks/useFileUpload';
import { useSave } from '../../../Hooks/useSave';
import Global from '../../../Services/Common/Global';
import { useAppNavigate } from '../../../Hooks/back/useAppNavigate';
import { useBackStack } from '../../../Hooks/back/useBackStack';
import useFcmHandler from '../../../Hooks/useFcmHandler';

const TARGET_ROLES = new Set(['ADMIN', 'HEAD_MANAGER', 'BUSINESS_OWNER']);
const FCM_BULK_CHUNK = 100;

const normalizeGubunDtlArr = (v) => {
  if (Array.isArray(v)) return v;
  if (v == null || v === '') return [];
  if (typeof v === 'string') return v.split(',').filter(Boolean);
  return [String(v)];
};

/**
 * GetCompanyInfo 응답: (1) 회사 단위 { coCd, coNm, custList: [{ custCd, custNm }, ...] }
 * (2) 사업장이 최상위에 바로 붙는 형태 — 둘 다 지원
 */
const sitesFromCompanyInfoRows = (rows) => {
  if (!Array.isArray(rows)) return [];
  const out = [];
  for (const r of rows) {
    if (Array.isArray(r.custList) && r.custList.length > 0) {
      for (const c of r.custList) {
        const custCd = c.custCd ?? c.CustCd;
        const custNm = c.custNm ?? c.custName ?? c.CustNm ?? '';
        if (custCd) out.push({ custCd, custNm });
      }
    } else {
      const custCd = r.custCd ?? r.CustCd;
      const custNm = r.custNm ?? r.custName ?? r.CustNm ?? '';
      if (custCd) out.push({ custCd, custNm });
    }
  }
  return out;
};

/** depNm1>…>depNm4 중 depNm1만 갖는 최상위(본부) 행만 — 라벨은 depNm1 */
const isTopLevelDepRow = (r) => {
  const s = (v) => (v == null ? '' : String(v).trim());
  if (s(r?.depNm2) !== '' || s(r?.depNm3) !== '' || s(r?.depNm4) !== '')
    return false;
  if (s(r?.depNm1 ?? r?.DepNm1) === '') return false;
  return true;
};

const deptsToMultiItems = (rows) => {
  if (!Array.isArray(rows)) return [];
  const top = rows.filter(isTopLevelDepRow);
  const seen = new Set();
  const out = [];
  for (const r of top) {
    const n1 = String(r.depNm1 ?? r.DepNm1).trim();
    if (seen.has(n1)) continue;
    seen.add(n1);
    out.push({ value: r.depCd, name: n1 });
  }
  return out;
};

const custListFromCustInfo = (list) =>
  (list || []).map((item) => ({
    value: item.custCd ?? item.value,
    name: item.custNm ?? item.name,
  }));

export default function M_NOT_BASIC_02F() {
  const { userInfo, custInfo, companyInfo } = useSelector(
    (state) => state.BaseInfo
  );
  const { id } = useParams();
  const dispatch = useDispatch();
  const [errors, setErrors] = useState({});
  const [noticeInputs, setNoticeInputs] = useState(() =>
    createInitial(M_NOT_BASIC_02_Schema.fields)
  );
  const { navigate } = useAppNavigate();
  const sendFcm = useFcmHandler();
  const activeStep = useSelector((state) => state.common.activeStep);
  const bottomSheetState = useSelector((state) => state.bottomSheet);

  const [selectedCo, setSelectedCo] = useState('');
  const [gubunDtlItems, setGubunDtlItems] = useState([]);
  const [custListForDept, setCustListForDept] = useState([]);
  const [selectedCustForDept, setSelectedCustForDept] = useState('');
  const [targetLoading, setTargetLoading] = useState(false);

  const { mutateAsync: save1Async } = useSave1();
  const { mutateAsync: save2Async } = useSave2();
  const { mutateAsync: Delete1Async } = useDelete1();

  const coList = useMemo(() => {
    const list = Array.isArray(companyInfo) ? companyInfo : [];
    return list.map((c) => ({ value: c.coCd, name: c.coNm || c.coCd }));
  }, [companyInfo]);

  const isTargetSelectRole = TARGET_ROLES.has(userInfo?.authCd);
  const effectiveCoCd = useMemo(() => {
    if (userInfo?.authCd === 'ADMIN') {
      return selectedCo || userInfo?.coCd || '';
    }
    return userInfo?.coCd || '';
  }, [userInfo, selectedCo]);

  const {
    FileChange,
    DeletePreview,
    SaveLogicFile,
    previewList,
    setPreviewList,
  } = useFileUpload({
    oldFilePathField: 'image',
    newFileNameField: 'saveName',
    getTargetPath: (item, coCd) =>
      `NoticeBoard/${coCd}/NOT_BASIC_01_02/${noticeInputs.chaebunNo}`,
    multiple: true,
    maxCount: 10,
    fileResultType: 'array',
  });

  const loadDepartments = useCallback(
    async (custCd, coCd) => {
      if (!custCd) {
        setGubunDtlItems([]);
        return;
      }
      setTargetLoading(true);
      try {
        const rows = await fetchDepartments(custCd, coCd || effectiveCoCd);
        setGubunDtlItems(deptsToMultiItems(rows));
      } catch (e) {
        console.error(e);
        setGubunDtlItems([]);
        dispatch(
          showSnackbar({
            message: '부서 목록을 불러오지 못했습니다.',
            type: 'error',
          })
        );
      } finally {
        setTargetLoading(false);
      }
    },
    [dispatch, effectiveCoCd]
  );

  const handleGubunChange = useCallback(
    async (newGubun) => {
      if (!isTargetSelectRole) return;

      if (userInfo?.authCd === 'ADMIN') {
        setNoticeInputs((prev) => ({
          ...prev,
          gubun: newGubun,
          gubunDtl: [],
          gubunDtlNm: [],
        }));
        setSelectedCustForDept('');

        const coCd = selectedCo || userInfo?.coCd;
        if (!coCd) {
          setGubunDtlItems([]);
          setCustListForDept([]);
          return;
        }

        if (newGubun === '0') {
          setGubunDtlItems([]);
          setCustListForDept([]);
          return;
        }

        setTargetLoading(true);
        try {
          const raw = await fetchCompanySites(coCd);
          const siteRows = sitesFromCompanyInfoRows(raw);
          if (!siteRows.length) {
            dispatch(
              showSnackbar({
                message: '해당 회사에 사업장 정보가 비어있습니다.',
                type: 'error',
              })
            );
            setGubunDtlItems([]);
            setCustListForDept([]);
            return;
          }
          if (newGubun === '1') {
            setGubunDtlItems(
              siteRows.map((s) => ({ value: s.custCd, name: s.custNm }))
            );
            setCustListForDept([]);
          } else if (newGubun === '2') {
            setCustListForDept(
              siteRows.map((s) => ({ value: s.custCd, name: s.custNm }))
            );
            setGubunDtlItems([]);
            setSelectedCustForDept('');
          }
        } catch (e) {
          console.error(e);
        } finally {
          setTargetLoading(false);
        }
        return;
      }

      if (newGubun === '0') {
        setGubunDtlItems([]);
        setNoticeInputs((prev) => ({
          ...prev,
          gubun: newGubun,
          gubunDtl: [],
          gubunDtlNm: [],
        }));
        setCustListForDept([]);
        setSelectedCustForDept('');
        return;
      }
      if (newGubun === '1') {
        setCustListForDept([]);
        setSelectedCustForDept('');
        setGubunDtlItems(
          custListFromCustInfo(custInfo).map((c) => ({
            value: c.value,
            name: c.name,
          }))
        );
        setNoticeInputs((prev) => ({
          ...prev,
          gubun: newGubun,
          gubunDtl: [],
          gubunDtlNm: [],
        }));
        return;
      }
      if (newGubun === '2') {
        setNoticeInputs((prev) => ({
          ...prev,
          gubun: newGubun,
          gubunDtl: [],
          gubunDtlNm: [],
        }));
        if (userInfo?.authCd === 'HEAD_MANAGER') {
          setCustListForDept([]);
          const cd = userInfo.custCd || '';
          setSelectedCustForDept(cd);
          setNoticeInputs((p) => ({ ...p, custCd: cd }));
          await loadDepartments(cd, userInfo?.coCd);
        } else if (userInfo?.authCd === 'BUSINESS_OWNER') {
          setCustListForDept(custListFromCustInfo(custInfo));
          setSelectedCustForDept('');
          setGubunDtlItems([]);
        } else {
          setCustListForDept([]);
          setSelectedCustForDept('');
          setGubunDtlItems([]);
        }
      }
    },
    [
      isTargetSelectRole,
      userInfo,
      selectedCo,
      custInfo,
      dispatch,
      loadDepartments,
    ]
  );

  const handleCoChange = useCallback(
    async (coCd) => {
      const g = String(noticeInputs.gubun || '0');
      setSelectedCo(coCd);
      setSelectedCustForDept('');
      setNoticeInputs((p) => ({
        ...p,
        coCd: coCd || '',
        gubunDtl: [],
        gubunDtlNm: [],
      }));
      if (userInfo?.authCd === 'ADMIN' && coCd && g && g !== '0') {
        setCustListForDept([]);
        setGubunDtlItems([]);
        await handleGubunChange(g);
      }
    },
    [userInfo, noticeInputs.gubun, handleGubunChange]
  );

  const handleCustForDeptChange = useCallback(
    async (custCd) => {
      setSelectedCustForDept(custCd);
      setNoticeInputs((p) => ({
        ...p,
        custCd: custCd || '',
        gubunDtl: [],
        gubunDtlNm: [],
      }));
      if (userInfo?.authCd === 'ADMIN' && !custCd) {
        setGubunDtlItems([]);
        return;
      }
      await loadDepartments(
        custCd,
        userInfo?.authCd === 'ADMIN' ? selectedCo : userInfo?.coCd
      );
    },
    [userInfo, selectedCo, loadDepartments]
  );

  const initFromLoadedRow = useCallback(
    async (row) => {
      if (!row || !isTargetSelectRole) return;
      const g = String(row.gubun ?? '0');
      const gv = normalizeGubunDtlArr(row.gubunDtl);
      const gvn = normalizeGubunDtlArr(row.gubunDtlNm);
      const coCd = row.coCd || userInfo?.coCd;
      if (userInfo?.authCd === 'ADMIN' && coCd) {
        setSelectedCo(coCd);
      }

      if (g === '1') {
        if (userInfo?.authCd === 'ADMIN') {
          setTargetLoading(true);
          try {
            const raw = await fetchCompanySites(coCd);
            const siteRows = sitesFromCompanyInfoRows(raw);
            setGubunDtlItems(
              siteRows.map((s) => ({ value: s.custCd, name: s.custNm }))
            );
            setNoticeInputs((p) => ({ ...p, gubunDtl: gv, gubunDtlNm: gvn }));
          } catch (e) {
            console.error(e);
          } finally {
            setTargetLoading(false);
          }
        } else {
          setGubunDtlItems(
            custListFromCustInfo(custInfo).map((c) => ({
              value: c.value,
              name: c.name,
            }))
          );
        }
        return;
      }
      if (g === '2') {
        if (userInfo?.authCd === 'ADMIN') {
          setTargetLoading(true);
          try {
            const companyRows = await fetchCompanySites(coCd);
            const sites = sitesFromCompanyInfoRows(companyRows);
            setCustListForDept(
              sites.map((s) => ({ value: s.custCd, name: s.custNm }))
            );
            let custCd = row.custCd;
            if (!custCd && gv.length && sites.length) {
              for (const s of sites) {
                const deps = await fetchDepartments(s.custCd, coCd);
                const depSet = new Set((deps || []).map((d) => d.depCd));
                if (gv.every((id) => depSet.has(id))) {
                  custCd = s.custCd;
                  break;
                }
              }
              if (!custCd) custCd = sites[0].custCd;
            }
            setSelectedCustForDept(custCd || '');
            if (custCd) {
              await loadDepartments(custCd, coCd);
            }
            setNoticeInputs((p) => ({
              ...p,
              gubunDtl: gv,
              gubunDtlNm: gvn,
              custCd: custCd || p.custCd,
            }));
          } catch (e) {
            console.error(e);
          } finally {
            setTargetLoading(false);
          }
          return;
        }
        if (userInfo?.authCd === 'HEAD_MANAGER') {
          const c = row.custCd || userInfo.custCd;
          setSelectedCustForDept(c || '');
          setNoticeInputs((p) => ({
            ...p,
            gubunDtl: gv,
            gubunDtlNm: gvn,
            custCd: c || p.custCd,
          }));
          if (c) {
            await loadDepartments(c, userInfo?.coCd);
          }
          return;
        }
        if (userInfo?.authCd === 'BUSINESS_OWNER') {
          setCustListForDept(custListFromCustInfo(custInfo));
          const c = row.custCd || '';
          setSelectedCustForDept(c);
          setNoticeInputs((p) => ({
            ...p,
            gubunDtl: gv,
            gubunDtlNm: gvn,
            custCd: c,
          }));
          if (c) {
            await loadDepartments(c, userInfo?.coCd);
          }
        }
      }
    },
    [isTargetSelectRole, userInfo, custInfo, loadDepartments]
  );

  useEffect(() => {
    dispatch(NavBarInfoActions.setType('titleBar'));
    dispatch(NavBarInfoActions.setTitleName('공지 사항'));
    dispatch(CommonActions.setActiveStep(1));
  }, []);

  useBackStack({
    type: 'sheet',
    enabled: !bottomSheetState.bottom,
    onBack: () =>
      dispatch(BottomSheetActions.OpenBottomSheet({ type: 'backClickWor' })),
  });

  useEffect(() => {
    if (activeStep === 0) {
      setNoticeInputs(createInitial(M_NOT_BASIC_02_Schema.fields));
      setGubunDtlItems([]);
      setCustListForDept([]);
      setSelectedCustForDept('');
    }
  }, [activeStep]);

  // HEAD_MANAGER는 UI에서「사업장(1)」을 제거 — 구분 1이 남은 경우(구데이터) 전체(0)로 정리
  useEffect(() => {
    if (userInfo?.authCd !== 'HEAD_MANAGER') return;
    if (String(noticeInputs.gubun) !== '1') return;
    void handleGubunChange('0');
  }, [userInfo?.authCd, noticeInputs.gubun, handleGubunChange]);

  useEffect(() => {
    if (userInfo?.authCd !== 'ADMIN' || !coList.length) return;
    if (id) return;
    if (!selectedCo) {
      const first = coList[0].value;
      setSelectedCo(first);
      setNoticeInputs((p) => ({ ...p, coCd: first }));
    }
  }, [userInfo, coList, id, selectedCo]);

  const { run: Search1 } = useSearch1({
    onSuccess: (rows) => {
      if (!rows?.[0]) return;
      const r0 = rows[0];
      const rawG = String(r0.gubun ?? '0');
      const splitDtl = r0.gubunDtl ? r0.gubunDtl.split(',') : [];
      const splitNm = r0.gubunDtlNm ? r0.gubunDtlNm.split(',') : [];

      let displayGubun = rawG;
      let displayDtl = splitDtl;
      let displayDtlNm = splitNm;

      if (rawG === '3' && userInfo?.authCd === 'BUSINESS_OWNER') {
        displayGubun = '0';
        displayDtl = [];
        displayDtlNm = [];
      } else if (rawG === '3' && userInfo?.authCd === 'ADMIN') {
        displayGubun = '0';
        displayDtl = [];
        displayDtlNm = [];
      } else if (
        rawG === '1' &&
        userInfo?.authCd === 'HEAD_MANAGER' &&
        splitDtl.length === 1 &&
        splitDtl[0] === userInfo?.custCd
      ) {
        displayGubun = '0';
        displayDtl = [];
        displayDtlNm = [];
      }

      const noticeYnValue =
        String(r0.noticeYn ?? r0.NoticeYn ?? 'N').toUpperCase() === 'Y'
          ? 'Y'
          : 'N';
      const next = {
        ...createInitial(M_NOT_BASIC_02_Schema.fields),
        ...r0,
        endDate: r0.endDate
          ? dayjs(r0.endDate).format('YYYY-MM-DD')
          : dayjs().add(7, 'day').format('YYYY-MM-DD'),
        gubun: displayGubun,
        gubunDtl: displayDtl,
        gubunDtlNm: displayDtlNm,
        coCd: r0.coCd || userInfo?.coCd || '',
        custCd: r0.custCd || '',
        noticeYn: noticeYnValue,
      };
      setNoticeInputs(next);
      if (userInfo?.authCd === 'ADMIN' && displayGubun === '0' && rawG === '3') {
        const co = (r0.coCd || splitDtl[0] || '').trim();
        if (co) setSelectedCo(co);
      }
      if (isTargetSelectRole) {
        initFromLoadedRow({
          ...r0,
          gubun: displayGubun,
          gubunDtl: displayDtl.join(',') || '',
          gubunDtlNm: displayDtlNm.join(',') || '',
        });
      }
    },
  });

  const { run: Search2 } = useSearch2({
    onSuccess: (rows) => {
      const fileListData = rows.map((f) => ({
        ...f,
        preview: /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(f.fileName)
          ? `${Global.file.useUrl}files/${f.filePath}`
          : null,
        previewFilePath: f.filePath,
        deleted: false,
      }));
      setPreviewList(fileListData);
    },
  });

  useEffect(() => {
    if (!userInfo?.userid) return;
    if (id) {
      Search1({ ChaebunNo: id });
      Search2({ ChaebunNo: id });
    }
    // Search1/Search2는 useSearchMutaion의 안정 run 참조에 의존
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, userInfo?.userid]);

  const { run: Save1 } = useSave({
    menuId: 'M_NOT_BASIC_02',
    schema: M_NOT_BASIC_02_Schema,
    saveData: () => {
      let finalData = { ...noticeInputs };

      if (!isTargetSelectRole) {
        finalData.gubun = '1';
        finalData.gubunDtl = [userInfo?.custCd].filter(Boolean);
        finalData.gubunDtlNm = [userInfo?.custNm].filter(Boolean);
        finalData.coCd = userInfo?.coCd || finalData.coCd;
        return finalData;
      }

      const coForSave =
        userInfo?.authCd === 'ADMIN'
          ? selectedCo || finalData.coCd || userInfo?.coCd
          : userInfo?.coCd;
      finalData.coCd = coForSave || '';

      const uiG = String(finalData.gubun ?? '0');

      if (userInfo?.authCd === 'BUSINESS_OWNER' && uiG === '0') {
        const coCd = (finalData.coCd || userInfo?.coCd || '').trim();
        const list = Array.isArray(companyInfo) ? companyInfo : [];
        const coRow =
          list.find((c) => c.coCd === coCd) || list[0] || null;
        const coNm = (
          coRow?.coNm ||
          userInfo?.coNm ||
          userInfo?.companyNm ||
          ''
        ).trim() || coCd;
        return {
          ...finalData,
          gubun: '3',
          gubunDtl: coCd ? [coCd] : [],
          gubunDtlNm: coNm ? [coNm] : [],
          coCd,
        };
      }
      if (userInfo?.authCd === 'HEAD_MANAGER' && uiG === '0') {
        const c = userInfo.custCd;
        const n = userInfo.custNm || userInfo.custName || '';
        return {
          ...finalData,
          gubun: '1',
          gubunDtl: c ? [c] : [],
          gubunDtlNm: n ? [n] : [],
          coCd: finalData.coCd || userInfo?.coCd || '',
        };
      }

      /** ADMIN: 회사 선택 후 구분「전체」 → gubun 3, 상세 = 선택 회사 코드/명 */
      if (userInfo?.authCd === 'ADMIN' && uiG === '0') {
        const coCd = (selectedCo || finalData.coCd || userInfo?.coCd || '').trim();
        const fromCombo = (coList || []).find((c) => c.value === coCd);
        const fromCompany = Array.isArray(companyInfo) ? companyInfo : [];
        const coRow = fromCompany.find((c) => c.coCd === coCd) || null;
        const coNm =
          (fromCombo?.name || coRow?.coNm || '').trim() || coCd;
        return {
          ...finalData,
          gubun: '3',
          gubunDtl: coCd ? [coCd] : [],
          gubunDtlNm: coNm ? [coNm] : [],
          coCd,
        };
      }

      if (uiG === '0') {
        finalData.gubunDtl = [];
        finalData.gubunDtlNm = [];
      } else if (uiG === '2') {
        if (userInfo?.authCd === 'HEAD_MANAGER') {
          finalData.custCd =
            userInfo.custCd || noticeInputs.custCd || finalData.custCd;
        } else {
          /* ADMIN·BO: 부서(2) + 선택 사업장 custCd 동시 저장 */
          finalData.custCd =
            selectedCustForDept || noticeInputs.custCd || finalData.custCd;
        }
      }

      return finalData;
    },
    fileSave: async () => {
      await Promise.all(
        previewList
          .filter((item) => item.deleted && item.noticeFId)
          .map((item) =>
            Delete1Async([{ fileId: item.noticeFId }]).catch((e) =>
              console.error('파일 삭제 실패:', e)
            )
          )
      );
      const movedFiles = await SaveLogicFile();
      return { movedFiles: movedFiles || [] };
    },
    requestSave: async (rows) => {
      const result = await save1Async(rows);
      const chaebunNo = result.data?.[0]?.chaebunNo || noticeInputs.chaebunNo;
      if (rows[0]?.movedFiles?.length > 0) {
        const fileRecords = rows[0].movedFiles.map((item) => ({
          chaebunNo: chaebunNo,
          fileName: item.fileName,
          saveName: item.saveName,
          filePath: item.filePath,
        }));
        await save2Async(fileRecords);
      }
      return result;
    },
    onSuccess: async ({ saveDetailData, requestRows }) => {
      const savedRow = requestRows?.[0];
      const saved = saveDetailData?.[0];
      const chaebunNo =
        saved?.chaebunNo ??
        saved?.ChaebunNo ??
        saved?.data?.[0]?.chaebunNo ??
        saved?.data?.[0]?.ChaebunNo ??
        savedRow?.chaebunNo ??
        noticeInputs.chaebunNo;

      setNoticeInputs(createInitial(M_NOT_BASIC_02_Schema.fields));
      setPreviewList([]);
      setGubunDtlItems([]);
      setCustListForDept([]);
      setSelectedCustForDept('');
      navigate('/M_Notice');

      if (savedRow && chaebunNo && sendFcm.bulk) {
        void (async () => {
          try {
            const targetIds = await resolveNoticeFcmTargetIds(
              savedRow,
              userInfo?.userid
            );
            if (targetIds.length > 0) {
              const isEdit = Boolean(id);
              await sendFcm.bulk({
                targetUserIds: targetIds,
                title: isEdit ? '공지사항 수정' : '공지사항',
                body:
                  savedRow.title?.trim() ||
                  (isEdit
                    ? '공지 내용이 수정되었습니다.'
                    : '새 공지가 등록되었습니다.'),
                codeGroup: isEdit ? 'AL_003_002' : 'AL_003_001',
                route: `/M_Notice/detail/${chaebunNo}`,
                responseData: chaebunNo,
                chunkSize: FCM_BULK_CHUNK,
              });
            }
          } catch (e) {
            console.error('[Notice FCM] 발송 실패:', e);
          }
        })();
      }
    },
  });

  return (
    <div>
      <M_NOT_BASIC_02
        errors={errors}
        setErrors={setErrors}
        noticeInputs={noticeInputs}
        setNoticeInputs={setNoticeInputs}
        previewList={previewList}
        SaveLogic1={Save1}
        FileChange={FileChange}
        DeletePreview={DeletePreview}
        isTargetSelectRole={isTargetSelectRole}
        targetLoading={targetLoading}
        coList={coList}
        selectedCo={selectedCo}
        onCoChange={handleCoChange}
        gubunDtlItems={gubunDtlItems}
        custListForDept={custListForDept}
        selectedCustForDept={selectedCustForDept}
        onCustForDeptChange={handleCustForDeptChange}
        onGubunChange={handleGubunChange}
      />
    </div>
  );
}
